import { Head, router } from "@inertiajs/react"
import { CalendarCheck, SquarePen } from "lucide-react"
import { useState } from "react"

import { HpiDataTable, HpiFilterSelect, HpiPagination } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface VendorContact {
  id: number
  display_name: string
  city: string | null
  state: string | null
  phone: string | null
  cell_phone: string | null
  email: string | null
}

interface Vendor {
  id: number
  display_name: string
  company_name: string | null
  vendor_type: string | null
  vendor_type_group_id: number | null
  city: string | null
  state: string | null
  country: string | null
  office_phone: string | null
  cell_phone: string | null
  email: string | null
  status: number
  schedule: boolean
  contacts?: VendorContact[]
}

interface FilterOptions {
  vendor_types: { id: number; name: string }[]
  vendor_type_groups: { id: number; name: string }[]
  statuses: { value: string; label: string }[]
  countries: { id: number; name: string }[]
  states: { value: string; label: string }[]
}

interface Filters {
  search: string
  vendor_type_id: string
  group: string
  country_id: string
  state: string
  status: string
}

interface Props {
  vendors: Vendor[]
  filter_options: FilterOptions
  filters: Filters
  can_create: boolean
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Subcontractors", href: "/company/vendors" },
]
const PER_PAGE = 100

/* ── Type Group Filter Buttons ──────────────────────────── */

function TypeGroupButtons({
  groups,
  activeGroup,
  onGroupChange,
}: {
  groups: { id: number; name: string }[]
  activeGroup: string
  onGroupChange: (groupId: string) => void
}) {
  if (groups.length === 0) return null

  return (
    <div className="mb-4 flex flex-wrap gap-2">
      <button
        onClick={() => onGroupChange("")}
        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
          !activeGroup
            ? "border-hpi-dark-blue bg-hpi-dark-blue text-white"
            : "border-hpi-med-gray text-hpi-dark-gray hover:border-hpi-dark-blue hover:text-hpi-dark-blue"
        }`}
      >
        All
      </button>
      {groups.map((g) => (
        <button
          key={g.id}
          onClick={() => onGroupChange(String(g.id))}
          className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
            activeGroup === String(g.id)
              ? "border-hpi-dark-blue bg-hpi-dark-blue text-white"
              : "border-hpi-med-gray text-hpi-dark-gray hover:border-hpi-dark-blue hover:text-hpi-dark-blue"
          }`}
        >
          {g.name}
        </button>
      ))}
    </div>
  )
}

/* ── Search Bar ─────────────────────────────────────────── */

function SearchBar({
  filters,
  filterOptions,
}: {
  filters: Filters
  filterOptions: FilterOptions
}) {
  const [search, setSearch] = useState(filters.search)
  const [vendorTypeId, setVendorTypeId] = useState(filters.vendor_type_id)
  const [countryId, setCountryId] = useState(filters.country_id)
  const [state, setState] = useState(filters.state)
  const [status, setStatus] = useState(filters.status)

  const buildParams = () => {
    const params: Record<string, string> = {}
    if (search) params.search = search
    if (vendorTypeId) params.vendor_type_id = vendorTypeId
    if (countryId) params.country_id = countryId
    if (state) params.state = state
    if (status && status !== "active") params.status = status
    if (filters.group) params.group = filters.group
    return params
  }

  const handleUpdate = () => {
    router.get("/company/vendors", buildParams(), { preserveState: true })
  }

  const handleClear = () => {
    setSearch("")
    setVendorTypeId("")
    setCountryId("")
    setState("")
    setStatus("active")
    const params: Record<string, string> = {}
    if (filters.group) params.group = filters.group
    router.get("/company/vendors", params, { preserveState: true })
  }

  const hasFilters =
    search || vendorTypeId || countryId || state || status !== "active"

  return (
    <div className="bg-hpi-pale-gray mb-6 flex flex-wrap items-end gap-4 rounded-lg px-4 py-3">
      <div>
        <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
          Search
        </label>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleUpdate()}
          placeholder="Name, phone, email..."
          className="border-hpi-med-gray text-hpi-dark-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue h-8 w-48 rounded border bg-white px-2 text-sm focus:ring-1"
        />
      </div>
      <HpiFilterSelect
        label="Type"
        value={vendorTypeId}
        onChange={setVendorTypeId}
        options={filterOptions.vendor_types.map((t) => ({
          value: t.id,
          label: t.name,
        }))}
        placeholder="All Types"
      />
      {filterOptions.countries.length > 0 && (
        <HpiFilterSelect
          label="Country"
          value={countryId}
          onChange={setCountryId}
          options={filterOptions.countries.map((c) => ({
            value: c.id,
            label: c.name,
          }))}
          placeholder="All Countries"
        />
      )}
      {filterOptions.states.length > 0 && (
        <HpiFilterSelect
          label="State"
          value={state}
          onChange={setState}
          options={filterOptions.states}
          placeholder="All States"
        />
      )}
      <div>
        <label className="text-hpi-dark-gray mb-1 block text-xs font-medium">
          Status
        </label>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="border-hpi-med-gray text-hpi-dark-gray focus:border-hpi-active-blue focus:ring-hpi-active-blue rounded border bg-white py-1.5 pr-7 pl-2 text-sm focus:ring-1"
        >
          {filterOptions.statuses.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      <button
        onClick={handleUpdate}
        className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-dark-blue h-8 rounded border px-5 text-sm font-medium transition-colors hover:text-white"
      >
        Search
      </button>
      {hasFilters && (
        <button
          onClick={handleClear}
          className="text-hpi-med-gray hover:text-hpi-dark-gray h-8 px-3 text-sm transition-colors"
        >
          Clear
        </button>
      )}
    </div>
  )
}

/* ── Inline Contact Row ─────────────────────────────────── */

function ContactRow({ contact }: { contact: VendorContact }) {
  return (
    <div className="border-hpi-light-gray/50 text-hpi-med-gray flex items-start gap-3 border-b py-2 pl-8 last:border-b-0">
      <div className="min-w-0 flex-1 text-xs">
        <span className="text-hpi-dark-gray font-medium">
          {contact.display_name}
        </span>
      </div>
      <div className="hidden min-w-0 flex-1 text-xs md:block" />
      <div className="hidden min-w-0 flex-1 text-xs md:block">
        {[contact.city, contact.state].filter(Boolean).join(", ")}
      </div>
      <div className="hidden min-w-0 flex-1 text-xs md:block">
        {contact.phone ?? contact.cell_phone}
      </div>
      <div className="hidden min-w-0 flex-1 text-xs md:block">
        {contact.email}
      </div>
    </div>
  )
}

/* ── Main Component ─────────────────────────────────────── */

export default function Index({
  vendors,
  filter_options,
  filters,
  can_create,
}: Props) {
  const [page, setPage] = useState(1)
  const totalPages = Math.ceil(vendors.length / PER_PAGE)
  const pagedVendors = vendors.slice((page - 1) * PER_PAGE, page * PER_PAGE)

  const isInactive = filters.status === "inactive"
  const isAll = filters.status === "all"
  const statusLabel = isInactive ? "Inactive" : isAll ? "All" : "Active"

  const handleToggleStatus = () => {
    const params: Record<string, string> = {}
    if (filters.search) params.search = filters.search
    if (filters.vendor_type_id) params.vendor_type_id = filters.vendor_type_id
    if (filters.group) params.group = filters.group
    if (filters.country_id) params.country_id = filters.country_id
    if (isInactive) {
      // Switching back to active (default) — no status param needed
    } else {
      params.status = "inactive"
    }
    router.get("/company/vendors", params, { preserveState: false })
  }

  const handleGroupChange = (groupId: string) => {
    const params: Record<string, string> = {}
    if (groupId) params.group = groupId
    if (filters.search) params.search = filters.search
    if (filters.vendor_type_id) params.vendor_type_id = filters.vendor_type_id
    if (filters.country_id) params.country_id = filters.country_id
    if (filters.status && filters.status !== "active")
      params.status = filters.status
    router.get("/company/vendors", params, { preserveState: false })
  }

  const columns: HpiColumn<Vendor>[] = [
    {
      header: "Name",
      render: (v) => (
        <div>
          <span
            className={`font-medium ${v.status === 1 ? "text-hpi-med-gray" : "text-hpi-dark-gray"}`}
          >
            {v.display_name}
          </span>
          {v.schedule && (
            <CalendarCheck className="text-hpi-active-blue ml-1.5 inline-block h-3.5 w-3.5" />
          )}
          {v.status === 1 && (
            <span className="text-hpi-med-gray ml-1.5 text-xs italic">
              (inactive)
            </span>
          )}
        </div>
      ),
    },
    {
      header: "Type",
      render: (v) => (
        <span className={v.status === 1 ? "text-hpi-med-gray" : ""}>
          {v.vendor_type}
        </span>
      ),
      hideOnMobile: true,
    },
    {
      header: "Location",
      render: (v) => {
        const parts = [v.city, v.state].filter(Boolean)
        if (v.country) parts.push(v.country)
        return (
          <span className={v.status === 1 ? "text-hpi-med-gray" : ""}>
            {parts.join(", ")}
          </span>
        )
      },
      hideOnMobile: true,
    },
    {
      header: "Phone",
      render: (v) => (
        <span className={v.status === 1 ? "text-hpi-med-gray" : ""}>
          {v.office_phone ?? v.cell_phone}
        </span>
      ),
      hideOnMobile: true,
    },
    {
      header: "Email",
      render: (v) => (
        <span className={v.status === 1 ? "text-hpi-med-gray" : ""}>
          {v.email}
        </span>
      ),
      hideOnMobile: true,
    },
  ]

  // Build flat list of rows: vendor rows + contact sub-rows for rendering
  const hasContacts = pagedVendors.some(
    (v) => v.contacts && v.contacts.length > 0,
  )

  const actions = can_create
    ? [
        {
          label: "Add Subcontractor",
          icon: SquarePen,
          onClick: () => {
            const url = filters.group
              ? `/company/vendors/new?group=${filters.group}`
              : "/company/vendors/new"
            router.visit(url)
          },
        },
      ]
    : []

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`${statusLabel} Subcontractors`} />

      <div className="p-6">
        <TypeGroupButtons
          groups={filter_options.vendor_type_groups}
          activeGroup={filters.group}
          onGroupChange={handleGroupChange}
        />

        <SearchBar filters={filters} filterOptions={filter_options} />

        <HpiDataTable
          title={`${statusLabel} Subcontractors`}
          actions={actions}
          columns={columns}
          data={pagedVendors}
          keyFn={(v) => v.id}
          rowHref={(v) => `/company/vendors/${v.id}`}
          pagination={
            vendors.length > PER_PAGE ? (
              <HpiPagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={vendors.length}
                perPage={PER_PAGE}
                onPageChange={setPage}
              />
            ) : undefined
          }
        />

        {/* Inline contact results under each vendor when searching */}
        {hasContacts && (
          <div className="border-hpi-light-gray mt-0 border-t bg-white px-4 pb-2">
            {pagedVendors
              .filter((v) => v.contacts && v.contacts.length > 0)
              .map((v) => (
                <div key={`contacts-${v.id}`} className="mt-1">
                  <div className="text-hpi-med-gray pl-8 text-[10px] font-medium tracking-wide uppercase">
                    Contacts for {v.display_name}
                  </div>
                  {v.contacts!.map((c) => (
                    <ContactRow key={c.id} contact={c} />
                  ))}
                </div>
              ))}
          </div>
        )}

        {!isAll && (
          <div className="mt-4 text-center">
            <button
              onClick={handleToggleStatus}
              className="text-hpi-active-blue hover:text-hpi-dark-blue text-sm font-medium transition-colors"
            >
              {isInactive ? "View Active" : "View Inactive"}
            </button>
          </div>
        )}
      </div>
    </AppLayout>
  )
}
